import { authedFetch } from './db.ts';
import { AnnouncementBanner } from '../types.ts';

const DISMISSED_KEY = 'crm_dismissed_announcements';

export function getLocalDismissedIds(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveLocalDismissedId(id: string): void {
  try {
    const current = getLocalDismissedIds();
    if (!current.includes(id)) {
      const updated = [...current, id];
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(updated));
    }
  } catch {
    /* ignore */
  }
}

export function clearLocalDismissedId(id: string): void {
  try {
    const current = getLocalDismissedIds();
    const updated = current.filter(item => item !== id);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(updated));
  } catch {
    /* ignore */
  }
}

export const DEFAULT_SPOTLIGHT_V186: AnnouncementBanner = {
  id: 'ann-spotlight-v186',
  title: '🚀 What’s New in Armellini CRM v1.8.6!',
  message: 'Milestone-Driven Pipeline Status & Category Engine, Edit CRM Lead Details Modal, Editable Approved Lanes with Full Activity Audit Logging, and Updated Rate Agreement Roles & Service Frequencies.',
  tone: 'feature',
  displayMode: 'both',
  isActive: true,
  targetRoles: [],
  targetDepartmentIds: [],
  targetDepartmentNames: [],
  targetUserEmails: [],
  featureItems: [
    {
      id: 'feat-v186-1',
      iconName: 'Workflow',
      title: 'Milestone-Driven Pipeline Status & Read-Only Badges',
      description: 'Status and Category now update automatically from pipeline milestone dates (Quoted, Approved, and Finalizing) with refreshed status badge colors. List View and Lead Drawer status badges are now strictly read-only, while Credit Application submissions log to Activity History and Quick Notes.',
      badge: 'IMPROVED',
      actionLabel: 'Open Dashboard',
      actionUrl: 'dashboard'
    },
    {
      id: 'feat-v186-2',
      iconName: 'Wrench',
      title: 'Edit CRM Lead Details Modal with Audit Logging',
      description: 'Edit Company Name, Lead Type (Customer / Shipper), Commodity Type, Primary Contact, Phone, Email, Address, and Current Freight Solution directly from the Lead Drawer with automatic before-and-after Activity History logging.',
      badge: 'NEW',
      actionLabel: 'Open Dashboard',
      actionUrl: 'dashboard'
    },
    {
      id: 'feat-v186-3',
      iconName: 'Truck',
      title: 'Editable Approved Lanes & Synced Quoting Dates',
      description: 'Approved lanes can now be edited or removed with confirmation and full field-level Activity History audit logs (old value, new value, date, and user). Quoted Date and Sent to Pricing Date are now read-only in the Dates tab and synced from lane activity.',
      badge: 'IMPROVED',
      actionLabel: 'Open Dashboard',
      actionUrl: 'dashboard'
    },
    {
      id: 'feat-v186-4',
      iconName: 'FileText',
      title: 'Rate Agreement Role & Service Frequency Updates',
      description: 'Added standalone "Bill To" alongside "Bill To/Consignee" and "Bill To/Shipper" in Rate Agreement Role dropdowns across Lane Quoting and Client Forms, and streamlined Service Frequency options by removing Bi-Weekly.',
      badge: 'NEW',
      actionLabel: 'View Client Forms',
      actionUrl: 'client-forms'
    }
  ],
  ctaLabel: "See What's New",
  ctaAction: 'open_modal',
  dismissible: true,
  createdAt: '2026-10-28T19:30:00.000Z',
  createdBy: 'msterling@armellini.com',
  createdByName: 'Mark Sterling',
  dismissedBy: []
};

export const DEFAULT_SPOTLIGHT_V182 = DEFAULT_SPOTLIGHT_V186;
export const DEFAULT_SPOTLIGHT_V174 = DEFAULT_SPOTLIGHT_V186;

export async function fetchAnnouncements(): Promise<AnnouncementBanner[]> {
  try {
    const res = await authedFetch('/api/announcements');
    if (!res.ok) {
      // Offline fallback from local cache if present
      const cached = localStorage.getItem('crm_announcements_cache');
      const list: AnnouncementBanner[] = cached ? JSON.parse(cached) : [];
      if (!list.some(a => a.id === DEFAULT_SPOTLIGHT_V186.id)) {
        list.unshift(DEFAULT_SPOTLIGHT_V186);
      }
      return list;
    }
    const data: AnnouncementBanner[] = await res.json();
    const list = Array.isArray(data) ? [...data] : [];
    if (!list.some(a => a.id === DEFAULT_SPOTLIGHT_V186.id)) {
      list.unshift(DEFAULT_SPOTLIGHT_V186);
    }
    try {
      localStorage.setItem('crm_announcements_cache', JSON.stringify(list));
    } catch {
      /* ignore */
    }
    return list;
  } catch (error) {
    console.warn('Failed to fetch announcements, returning cached fallback:', error);
    try {
      const cached = localStorage.getItem('crm_announcements_cache');
      const list: AnnouncementBanner[] = cached ? JSON.parse(cached) : [];
      if (!list.some(a => a.id === DEFAULT_SPOTLIGHT_V186.id)) {
        list.unshift(DEFAULT_SPOTLIGHT_V186);
      }
      return list;
    } catch {
      return [DEFAULT_SPOTLIGHT_V186];
    }
  }
}

export async function saveAnnouncement(announcement: AnnouncementBanner): Promise<AnnouncementBanner> {
  const isNew = !announcement.id;
  const url = isNew ? '/api/announcements' : `/api/announcements/${announcement.id}`;
  const method = isNew ? 'POST' : 'PUT';

  const res = await authedFetch(url, {
    method,
    body: JSON.stringify(announcement),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to save announcement' }));
    throw new Error(err.error || 'Failed to save announcement');
  }

  const result = await res.json();
  return result.announcement || announcement;
}

export async function deleteAnnouncementApi(id: string): Promise<void> {
  const res = await authedFetch(`/api/announcements/${id}`, {
    method: 'DELETE',
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to delete announcement' }));
    throw new Error(err.error || 'Failed to delete announcement');
  }
}

export async function dismissAnnouncementApi(id: string): Promise<void> {
  saveLocalDismissedId(id);
  try {
    await authedFetch(`/api/announcements/${id}/dismiss`, {
      method: 'POST',
    });
  } catch (error) {
    console.warn('Failed to sync dismissal to server, recorded locally:', error);
  }
}
